import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dice5, Coins, Gift, Trophy, LogOut, Play, Loader2, ChevronRight, AlertTriangle, Sparkles, ShoppingBag, Flame, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Fliboe Play — Social play-money casino MVP.
 * Isolated route tree: /casino, /casino/play. NO nav ties to FM / Flip.
 * Own storage key `fliboe_casino_token` so users can log in here without
 * leaking any FM/Flip broadcaster session state, and vice-versa.
 *
 * Legal posture: NOT gambling. Coins are virtual, non-transferable, cannot
 * be redeemed for money or prizes. 18+ age gate before play.
 */
const CASINO_TOKEN_KEY = "fliboe_casino_token";
const CASINO_AGE_KEY = "fliboe_casino_age_18";

function casinoAuthHeaders() {
  const t = localStorage.getItem(CASINO_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ────────────────────────────────────────────────────────────
// Public landing (route: /casino)
// ────────────────────────────────────────────────────────────
export function CasinoLanding() {
  return (
    <main className="min-h-screen bg-[#0a0505] text-white" data-testid="casino-landing">
      <CasinoHeader />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-950 via-black to-yellow-950 opacity-70" />
        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-24 sm:pt-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-yellow-500 mb-4">// Fliboe Play · Social casino</div>
            <h1 className="fl-display text-5xl sm:text-7xl font-black tracking-tighter leading-[0.9]">
              PLAY FOR<br />
              <span className="text-yellow-500">FUN.</span>{" "}
              <span className="text-red-500">NOT MONEY.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed">
              Fliboe Play is a social slots lounge. Every spin uses virtual coins that <strong className="text-yellow-400">cannot</strong> be redeemed for cash or prizes. Just for fun, on the house. Claim 500 free coins every day.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/casino/play" className="fl-btn !bg-yellow-500 !text-black hover:!bg-yellow-400" data-testid="casino-landing-play">
                <Play className="w-4 h-4" /> Play now
              </Link>
              <Link to="/casino/login" className="fl-btn ghost" data-testid="casino-landing-signin">Sign in</Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
              {["18+ only", "Play money", "No cash value", "No prizes"].map(c => (
                <span key={c} className="px-3 py-1 rounded-full border border-yellow-500/30 bg-black/40 text-yellow-300">{c}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <GinnieHero />
          </div>
        </div>
      </section>

      <CasinoLegalFooter />
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// Dancing genie hero — CSS-animated 🧞‍♀️ + Web Audio-synthesized
// belly-dance melody on the Double Harmonic (Arabic) minor scale.
// Zero assets, zero deps. Muted by default so autoplay policies are happy.
// Replace `playArabicLoop` with a real audio file when you have one:
//   <audio src="/path/to/your.mp3" loop /> and toggle its .play() / .pause().
// ────────────────────────────────────────────────────────────
function GinnieHero() {
  const [playing, setPlaying] = useState(false);
  const loopRef = useRef(null);
  const toggleMusic = () => {
    if (playing) { stopArabicLoop(loopRef); setPlaying(false); }
    else { playArabicLoop(loopRef); setPlaying(true); }
    SOUND.click();
  };
  useEffect(() => () => stopArabicLoop(loopRef), []);

  return (
    <div className="fl-card p-8 bg-gradient-to-br from-purple-900/60 via-black/70 to-yellow-900/40 border-yellow-500/40 text-center relative overflow-hidden" data-testid="ginnie-hero">
      {/* Sparkle particles */}
      <style>{`
        @keyframes ginnie-sway { 0%,100% { transform: translateX(-6px) rotate(-3deg) scale(1); } 50% { transform: translateX(6px) rotate(3deg) scale(1.03); } }
        @keyframes ginnie-shimmer { 0%,100% { filter: drop-shadow(0 0 12px #34d399) drop-shadow(0 0 24px #a855f7); } 50% { filter: drop-shadow(0 0 20px #10b981) drop-shadow(0 0 40px #c084fc); } }
        @keyframes ginnie-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
        @keyframes ginnie-sparkle { 0% { opacity: 0; transform: translate(0,0) scale(0.4) } 30% { opacity: 1 } 100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1) } }
        .ginnie-dance { animation: ginnie-sway 1.4s ease-in-out infinite, ginnie-shimmer 2.2s ease-in-out infinite; transform-origin: 50% 90%; }
        .ginnie-container { animation: ginnie-float 3s ease-in-out infinite; }
      `}</style>
      {[...Array(14)].map((_, i) => (
        <div key={i} className="absolute pointer-events-none" style={{
          left: `${(i * 37) % 90 + 5}%`, top: `${(i * 53) % 80 + 10}%`,
          fontSize: `${8 + (i % 3) * 4}px`,
          color: i % 2 ? "#34d399" : "#c084fc",
          animation: `ginnie-sparkle 2.${4 + (i % 6)}s ease-out ${i * 0.18}s infinite`,
          "--dx": `${(i % 5 - 2) * 20}px`, "--dy": `${-30 - (i % 4) * 10}px`,
        }}>✦</div>
      ))}

      <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-yellow-400 mb-2 relative z-10">// The genie is dancing</div>
      <div className="ginnie-container relative z-10">
        <div className="ginnie-dance text-[7.5rem] leading-none inline-block select-none" role="img" aria-label="Dancing genie">🧞‍♀️</div>
      </div>
      <div className="mt-4 text-2xl font-black text-yellow-400 relative z-10">Fliboe Slots</div>
      <div className="mt-1 text-white/50 text-sm relative z-10">Match 3 to win up to 100× your bet</div>

      <button onClick={toggleMusic} className="fl-btn ghost mt-5 !py-2 !px-4 text-xs relative z-10 !border-yellow-400/40 !text-yellow-200 hover:!bg-yellow-500/10" data-testid="ginnie-music-toggle">
        {playing ? "⏸ Pause music" : "🎵 Play \"Inside Your Pocket\""}
      </button>
    </div>
  );
}

// Belly-dance track (user-provided). Plays the first 30 seconds on loop.
const BELLY_DANCE_TRACK_URL = "https://customer-assets-v7afamib.emergentagent.net/job_budget-app-factory/artifacts/7nn7l0mz_in-your-pocket.mp3";
const BELLY_DANCE_CLIP_SEC = 30;

function playArabicLoop(loopRef) {
  if (typeof Audio === "undefined") return;
  if (loopRef.current) return;
  const audio = new Audio(BELLY_DANCE_TRACK_URL);
  audio.volume = 0.55;
  audio.preload = "auto";
  // Trim to first 30s, then rewind and keep looping
  const onTime = () => { if (audio.currentTime >= BELLY_DANCE_CLIP_SEC) { audio.currentTime = 0; } };
  audio.addEventListener("timeupdate", onTime);
  audio.play().catch(() => { /* browser blocked autoplay — click again */ });
  loopRef.current = { audio, onTime };
}
function stopArabicLoop(loopRef) {
  const s = loopRef.current; if (!s) return;
  try { s.audio.pause(); s.audio.removeEventListener("timeupdate", s.onTime); } catch (_) { /* ignore */ }
  loopRef.current = null;
}

// ────────────────────────────────────────────────────────────
// Casino-branded login (route: /casino/login)
// Reuses backend /auth/login + /auth/register but stores token in casino key
// ────────────────────────────────────────────────────────────
export function CasinoLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [confirmAge, setConfirmAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOk, setForgotOk] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (mode === "forgot") {
      if (!email.trim()) { setErr("Enter your email to reset password."); return; }
      setBusy(true);
      try {
        await api.post("/auth/forgot-password", { email });
        setForgotOk(true);
      } catch (ex) { setErr(ex?.response?.data?.detail || "Could not send reset email."); }
      finally { setBusy(false); }
      return;
    }
    if (!confirmAge) { setErr("You must confirm you are 18 or older."); return; }
    if (!email.trim() || password.length < 6) { setErr("Email and 6+ char password required."); return; }
    setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const body = mode === "signup" ? { email, password, name } : { email, password };
      const { data } = await api.post(path, body);
      if (!data.session_token) throw new Error("No token returned");
      localStorage.setItem(CASINO_TOKEN_KEY, data.session_token);
      localStorage.setItem(CASINO_AGE_KEY, "1");
      navigate("/casino/play");
    } catch (ex) { setErr(ex?.response?.data?.detail || "Login failed. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#0a0505] text-white" data-testid="casino-login-page">
      <CasinoHeader />
      <section className="max-w-md mx-auto px-5 py-16">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-yellow-500 mb-3 text-center">// Fliboe Play</div>
        <h1 className="fl-display text-3xl font-black text-center">{mode === "signup" ? "Create your Play account" : mode === "forgot" ? "Reset your password" : "Welcome back, player"}</h1>
        <form onSubmit={submit} className="fl-card p-6 mt-6 space-y-3 bg-black/60 border-yellow-500/20">
          {mode === "forgot" && forgotOk ? (
            <div className="text-emerald-300 text-sm text-center py-4" data-testid="casino-forgot-ok">
              If <strong>{email}</strong> matches an account, we&apos;ve emailed a reset link. Check your inbox (and spam).
              <button type="button" onClick={() => { setMode("login"); setForgotOk(false); setErr(""); }} className="block mx-auto mt-4 text-yellow-300 text-xs hover:underline" data-testid="casino-forgot-back">
                Back to sign in
              </button>
            </div>
          ) : (
          <>
          {mode === "signup" && (
            <input className="fl-input w-full" placeholder="Nickname (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="casino-login-name" />
          )}
          <input className="fl-input w-full" placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="casino-login-email" />
          {mode !== "forgot" && (
            <input className="fl-input w-full" placeholder="Password (6+ chars)" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="casino-login-password" />
          )}
          {mode !== "forgot" && (
            <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={confirmAge} onChange={e => setConfirmAge(e.target.checked)} data-testid="casino-login-age" />
              <span>I confirm I am 18 or older and understand this is a social game — coins have no cash value and cannot be redeemed.</span>
            </label>
          )}
          {err && <div className="text-red-400 text-sm" data-testid="casino-login-err">{err}</div>}
          <button type="submit" disabled={busy} className="fl-btn w-full justify-center !bg-yellow-500 !text-black hover:!bg-yellow-400" data-testid="casino-login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {mode === "signup" ? "Create & play" : mode === "forgot" ? "Send reset email" : "Sign in & play"}
          </button>

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!confirmAge) { setErr("Confirm 18+ first, then continue with Google."); return; }
                  localStorage.setItem(CASINO_AGE_KEY, "1");
                  const redirectUrl = window.location.origin + "/casino/auth-callback";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-white/90 flex items-center justify-center gap-2 transition"
                data-testid="casino-login-google"
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Sign in with Google
              </button>
            </>
          )}

          <div className="flex items-center justify-between gap-3 mt-2">
            <button type="button" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setErr(""); }} className="text-yellow-300 text-xs hover:underline" data-testid="casino-login-toggle">
              {mode === "signup" ? "Have an account? Sign in" : mode === "forgot" ? "Back to sign in" : "New here? Create account"}
            </button>
            {mode !== "forgot" && (
              <button type="button" onClick={() => { setMode("forgot"); setErr(""); }} className="text-white/50 text-xs hover:text-yellow-300 hover:underline" data-testid="casino-login-forgot">
                Forgot password?
              </button>
            )}
          </div>
          </>
          )}
        </form>
      </section>
      <CasinoLegalFooter />
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// Sound + Confetti utilities (no external deps)
// ────────────────────────────────────────────────────────────
let _audioCtx = null;
function _ac() {
  if (typeof window === "undefined") return null;
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch (_) { return null; }
  }
  return _audioCtx;
}
function playTone(freq, duration = 0.15, type = "sine", volume = 0.15) {
  const ctx = _ac(); if (!ctx) return;
  const osc = ctx.createOscillator(); const gain = ctx.createGain();
  osc.type = type; osc.frequency.value = freq;
  gain.gain.setValueAtTime(volume, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  osc.connect(gain); gain.connect(ctx.destination);
  osc.start(); osc.stop(ctx.currentTime + duration);
}
const SOUND = {
  spin:  () => { [220, 260, 300, 340].forEach((f, i) => setTimeout(() => playTone(f, 0.08, "square"), i * 60)); },
  click: () => playTone(600, 0.05, "square", 0.08),
  win:   () => { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => playTone(f, 0.12, "triangle", 0.18), i * 80)); },
  bigwin:() => { [392, 494, 587, 784, 988].forEach((f, i) => setTimeout(() => playTone(f, 0.2, "sawtooth", 0.2), i * 100)); },
  loss:  () => { [220, 165].forEach((f, i) => setTimeout(() => playTone(f, 0.15, "sine", 0.1), i * 120)); },
};
function fireConfetti() {
  if (typeof document === "undefined") return;
  const colors = ["#facc15", "#059669", "#ef4444", "#10b981", "#3b82f6", "#a855f7"];
  const root = document.createElement("div");
  root.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden";
  document.body.appendChild(root);
  for (let i = 0; i < 80; i++) {
    const el = document.createElement("div");
    const x = 50 + (Math.random() - 0.5) * 30;
    const angle = (Math.random() * 360);
    const dist = 200 + Math.random() * 300;
    const dx = Math.cos(angle * Math.PI/180) * dist;
    const dy = Math.sin(angle * Math.PI/180) * dist + 400; // gravity bias
    el.style.cssText = `position:absolute;left:${x}%;top:40%;width:10px;height:14px;background:${colors[i % colors.length]};transform:rotate(${Math.random()*360}deg);transition:transform 1.6s cubic-bezier(.2,.7,.4,1),opacity 1.6s;`;
    root.appendChild(el);
    requestAnimationFrame(() => {
      el.style.transform = `translate(${dx}px, ${dy}px) rotate(${Math.random()*720}deg)`;
      el.style.opacity = "0";
    });
  }
  setTimeout(() => root.remove(), 1700);
}

// ────────────────────────────────────────────────────────────
// Play area (route: /casino/play) — slots + blackjack + roulette + shop
// ────────────────────────────────────────────────────────────
export function CasinoPlay() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [board, setBoard] = useState([]);
  const [game, setGame] = useState("slots"); // slots | blackjack | roulette
  const [shopOpen, setShopOpen] = useState(false);

  const refresh = async () => {
    try {
      const [w, l] = await Promise.all([
        api.get("/casino/wallet", { headers: casinoAuthHeaders() }),
        api.get("/casino/leaderboard"),
      ]);
      setWallet(w.data);
      setBoard(l.data || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/casino/login");
    }
  };
  useEffect(() => {
    if (!localStorage.getItem(CASINO_TOKEN_KEY)) navigate("/casino/login");
    else refresh();
  }, []);

  if (!wallet) return (
    <main className="min-h-screen bg-[#0a0505] text-white grid place-items-center" data-testid="casino-play-loading">
      <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
    </main>
  );

  const logout = () => { localStorage.removeItem(CASINO_TOKEN_KEY); navigate("/casino"); };

  return (
    <main className="min-h-screen bg-[#0a0505] text-white" data-testid="casino-play">
      <CasinoHeader onLogout={logout} balance={wallet.coin_balance} />

      {/* Game tabs */}
      <div className="max-w-4xl mx-auto px-5 pt-6">
        <div className="flex gap-2 flex-wrap" data-testid="casino-game-tabs">
          {[
            { id: "slots",     label: "🎰 Slots" },
            { id: "blackjack", label: "🃏 Blackjack" },
            { id: "roulette",  label: "🎡 Roulette" },
          ].map(t => (
            <button key={t.id} onClick={() => { SOUND.click(); setGame(t.id); }} className={`px-4 py-2 rounded-full text-sm font-bold transition ${game === t.id ? "bg-yellow-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`casino-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
          <button onClick={() => { SOUND.click(); setShopOpen(true); }} className="px-4 py-2 rounded-full text-sm font-bold bg-red-500 text-white hover:bg-red-400 ml-auto flex items-center gap-1" data-testid="casino-shop-open">
            <ShoppingBag className="w-4 h-4" /> Buy coins
          </button>
        </div>
      </div>

      <section className="max-w-4xl mx-auto px-5 py-6 grid md:grid-cols-3 gap-6">
        {/* Left: current game */}
        <div className="md:col-span-2">
          {game === "slots"     && <SlotsGame wallet={wallet} setWallet={setWallet} onRefresh={refresh} />}
          {game === "blackjack" && <BlackjackGame wallet={wallet} setWallet={setWallet} onRefresh={refresh} />}
          {game === "roulette"  && <RouletteGame wallet={wallet} setWallet={setWallet} onRefresh={refresh} />}
        </div>

        {/* Right: wallet + streak + leaderboard */}
        <div className="space-y-4">
          <WalletCard wallet={wallet} onClaimed={(data) => { setWallet(w => ({ ...w, coin_balance: data.new_balance, daily_streak: data.streak, last_daily_claim: new Date().toISOString() })); }} />
          <div className="fl-card p-5 bg-black/70 border-yellow-500/20">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-4 h-4 text-yellow-500" />
              <div className="fl-mono text-[10px] uppercase tracking-widest text-yellow-500">Top winners</div>
            </div>
            <ol className="space-y-1.5" data-testid="casino-leaderboard">
              {board.length === 0 && <li className="text-white/40 text-xs">No spins yet — be the first.</li>}
              {board.map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className={`w-5 text-center font-bold ${i === 0 ? "text-yellow-400" : "text-white/50"}`}>{i+1}</span>
                  <span className="text-white truncate flex-1">{p.name}</span>
                  <span className="text-yellow-400 font-bold">{p.total_won.toLocaleString()}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {shopOpen && <CoinShop onClose={() => setShopOpen(false)} onPurchased={refresh} />}
      <CasinoLegalFooter />
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// Wallet card with streak + daily claim ladder
// ────────────────────────────────────────────────────────────
function WalletCard({ wallet, onClaimed }) {
  const [claiming, setClaiming] = useState(false);
  const [msg, setMsg] = useState("");
  const streak = wallet.daily_streak || 0;
  const nextGrant = { 0: 500, 1: 750, 2: 1000 }[streak] || 2000;
  const claim = async () => {
    setClaiming(true); setMsg("");
    try {
      const { data } = await api.post("/casino/daily-claim", {}, { headers: casinoAuthHeaders() });
      onClaimed(data);
      SOUND.win();
      setMsg(`+${data.granted} · Day ${data.streak} streak`);
    } catch (e) { setMsg(e?.response?.data?.detail || "Cannot claim yet"); }
    finally { setClaiming(false); setTimeout(() => setMsg(""), 3500); }
  };
  return (
    <div className="fl-card p-5 bg-black/70 border-yellow-500/20">
      <div className="flex items-center gap-2">
        <Coins className="w-4 h-4 text-yellow-500" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-yellow-500">Wallet</div>
      </div>
      <div className="mt-2 text-4xl font-black text-yellow-400" data-testid="casino-balance">{wallet.coin_balance.toLocaleString()}</div>
      <div className="text-white/40 text-xs mt-1">virtual coins · no cash value</div>

      {/* Streak visualization */}
      <div className="mt-4">
        <div className="flex items-center gap-1 text-xs text-white/60 mb-2">
          <Flame className={`w-3.5 h-3.5 ${streak > 0 ? "text-emerald-400" : "text-white/30"}`} />
          <span>Day {streak || 1} streak · next {nextGrant}</span>
        </div>
        <div className="flex gap-1" data-testid="casino-streak-dots">
          {[1,2,3,4].map(d => (
            <div key={d} className={`h-1.5 flex-1 rounded-full ${d <= streak ? "bg-emerald-400" : "bg-white/10"}`} />
          ))}
        </div>
      </div>

      <button onClick={claim} disabled={claiming} className="fl-btn ghost w-full mt-4 !py-2 text-sm" data-testid="casino-daily-claim">
        {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Claim daily {nextGrant}
      </button>
      {msg && <div className="text-yellow-300 text-xs mt-2" data-testid="casino-claim-msg">{msg}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Slots
// ────────────────────────────────────────────────────────────
function SlotsGame({ wallet, setWallet, onRefresh }) {
  const [reels, setReels] = useState(["🎰", "🎰", "🎰"]);
  const [bet, setBet] = useState(50);
  const [spinning, setSpinning] = useState(false);
  const [msg, setMsg] = useState("");
  const spin = async () => {
    if (spinning) return;
    if (wallet.coin_balance < bet) { setMsg("Not enough coins — claim your daily bonus."); return; }
    setSpinning(true); setMsg(""); SOUND.spin();
    const symbols = ["🍒", "🍋", "🍊", "🔔", "⭐", "💎", "7️⃣"];
    const iv = setInterval(() => setReels([symbols[Math.floor(Math.random()*7)], symbols[Math.floor(Math.random()*7)], symbols[Math.floor(Math.random()*7)]]), 90);
    try {
      const { data } = await api.post("/casino/slots/spin", { bet }, { headers: casinoAuthHeaders() });
      setTimeout(() => {
        clearInterval(iv);
        setReels(data.reels);
        setWallet(w => ({ ...w, coin_balance: data.new_balance }));
        if (data.win_kind === "triple") { setMsg(`🎉 TRIPLE ${data.reels[0]}! +${data.payout} coins`); SOUND.bigwin(); fireConfetti(); }
        else if (data.win_kind === "pair") { setMsg(`Nice pair — +${data.payout} coins`); SOUND.win(); }
        else { setMsg(`No luck — try again`); SOUND.loss(); }
        setSpinning(false); onRefresh();
      }, 1200);
    } catch (e) { clearInterval(iv); setSpinning(false); setMsg(e?.response?.data?.detail || "Spin failed"); }
  };
  return (
    <div className="fl-card p-6 md:p-8 bg-black/70 border-yellow-500/30 text-center" data-testid="slots-game">
      <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-yellow-500 mb-2">// Fliboe Slots</div>
      <h2 className="fl-display text-3xl font-black">Match 3 to win big.</h2>
      <div className="mt-6 grid grid-cols-3 gap-3" data-testid="slots-reels">
        {reels.map((r, i) => (
          <div key={i} className={`p-6 rounded-2xl bg-gradient-to-b from-white/10 to-black/40 border border-yellow-500/20 text-6xl transition-transform ${spinning ? "animate-pulse" : ""}`} data-testid={`slots-reel-${i}`}>{r}</div>
        ))}
      </div>
      <BetPicker bet={bet} setBet={setBet} disabled={spinning} testidPrefix="slots-bet" />
      <button onClick={spin} disabled={spinning || wallet.coin_balance < bet} className="fl-btn !bg-red-500 hover:!bg-red-400 !text-white mt-6 !py-3 !px-8 text-lg" data-testid="slots-spin">
        {spinning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Dice5 className="w-5 h-5" />} {spinning ? "Spinning…" : `Spin for ${bet}`}
      </button>
      {msg && <div className="mt-4 text-yellow-300 text-sm" data-testid="slots-msg">{msg}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Blackjack
// ────────────────────────────────────────────────────────────
function BlackjackGame({ wallet, setWallet, onRefresh }) {
  const [game, setGame] = useState(null);
  const [bet, setBet] = useState(100);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const start = async () => {
    if (busy) return;
    if (wallet.coin_balance < bet) { setMsg("Not enough coins."); return; }
    setBusy(true); setMsg(""); SOUND.click();
    try {
      const { data } = await api.post("/casino/blackjack/start", { bet }, { headers: casinoAuthHeaders() });
      setGame(data);
      setWallet(w => ({ ...w, coin_balance: w.coin_balance - bet }));
      if (data.status === "done") finishToast(data);
    } catch (e) { setMsg(e?.response?.data?.detail || "Start failed"); }
    finally { setBusy(false); }
  };
  const hit = async () => {
    setBusy(true); SOUND.click();
    try {
      const { data } = await api.post(`/casino/blackjack/${game.game_id}/hit`, {}, { headers: casinoAuthHeaders() });
      setGame(data);
      if (data.status === "done") finishToast(data);
    } catch (e) { setMsg(e?.response?.data?.detail || "Hit failed"); }
    finally { setBusy(false); }
  };
  const stand = async () => {
    setBusy(true); SOUND.click();
    try {
      const { data } = await api.post(`/casino/blackjack/${game.game_id}/stand`, {}, { headers: casinoAuthHeaders() });
      setGame(data);
      finishToast(data);
    } catch (e) { setMsg(e?.response?.data?.detail || "Stand failed"); }
    finally { setBusy(false); }
  };
  const finishToast = (g) => {
    onRefresh();
    if (g.result === "blackjack") { setMsg("🎉 BLACKJACK! +" + g.payout); SOUND.bigwin(); fireConfetti(); }
    else if (g.result === "win")  { setMsg("You win! +" + g.payout); SOUND.win(); }
    else if (g.result === "push") { setMsg("Push — bet returned"); SOUND.click(); }
    else if (g.result === "bust") { setMsg("Bust! -" + g.bet); SOUND.loss(); }
    else                          { setMsg("Dealer wins. -" + g.bet); SOUND.loss(); }
  };

  const active = game && game.status === "playing";

  return (
    <div className="fl-card p-6 md:p-8 bg-black/70 border-yellow-500/30" data-testid="blackjack-game">
      <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-yellow-500 mb-2">// Blackjack</div>
      <h2 className="fl-display text-3xl font-black">Get to 21. Beat the dealer.</h2>

      {game && (
        <div className="mt-6 space-y-4">
          <div>
            <div className="text-white/50 text-xs mb-1">Dealer{game.dealer_total ? ` · ${game.dealer_total}` : ""}</div>
            <div className="flex gap-2 flex-wrap">{game.dealer.map((c, i) => <Card key={i} face={c} />)}</div>
          </div>
          <div>
            <div className="text-white/50 text-xs mb-1">You · {game.player_total}</div>
            <div className="flex gap-2 flex-wrap">{game.player.map((c, i) => <Card key={i} face={c} />)}</div>
          </div>
        </div>
      )}
      {!game && (
        <>
          <BetPicker bet={bet} setBet={setBet} disabled={busy} testidPrefix="bj-bet" />
          <button onClick={start} disabled={busy || wallet.coin_balance < bet} className="fl-btn !bg-red-500 hover:!bg-red-400 !text-white mt-6 !py-3 !px-8" data-testid="bj-deal">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Dice5 className="w-5 h-5" />} Deal for {bet}
          </button>
        </>
      )}
      {active && (
        <div className="mt-6 flex gap-2">
          <button onClick={hit} disabled={busy} className="fl-btn !bg-yellow-500 !text-black" data-testid="bj-hit">Hit</button>
          <button onClick={stand} disabled={busy} className="fl-btn ghost" data-testid="bj-stand">Stand</button>
        </div>
      )}
      {game && game.status === "done" && (
        <button onClick={() => { setGame(null); setMsg(""); }} className="fl-btn ghost mt-4" data-testid="bj-newgame">New hand</button>
      )}
      {msg && <div className="mt-4 text-yellow-300 text-sm" data-testid="bj-msg">{msg}</div>}
    </div>
  );
}

function Card({ face }) {
  const red = face.includes("♥") || face.includes("♦");
  return (
    <div className={`inline-flex items-center justify-center w-12 h-16 sm:w-14 sm:h-20 rounded-lg bg-white text-2xl sm:text-3xl font-black shadow ${red ? "text-red-600" : "text-black"}`}>{face}</div>
  );
}

// ────────────────────────────────────────────────────────────
// Roulette
// ────────────────────────────────────────────────────────────
function RouletteGame({ wallet, setWallet, onRefresh }) {
  const [bet, setBet] = useState(50);
  const [betType, setBetType] = useState("red");
  const [number, setNumber] = useState(17);
  const [result, setResult] = useState(null); // {result, color, ...}
  const [spinning, setSpinning] = useState(false);
  const [msg, setMsg] = useState("");
  const [ballFrame, setBallFrame] = useState(0);
  const spin = async () => {
    if (spinning) return;
    if (wallet.coin_balance < bet) { setMsg("Not enough coins."); return; }
    setSpinning(true); setMsg(""); SOUND.spin();
    const iv = setInterval(() => setBallFrame(f => (f + 1) % 37), 60);
    try {
      const body = betType === "straight" ? { bet, bet_type: betType, number } : { bet, bet_type: betType };
      const { data } = await api.post("/casino/roulette/spin", body, { headers: casinoAuthHeaders() });
      setTimeout(() => {
        clearInterval(iv);
        setResult(data);
        setWallet(w => ({ ...w, coin_balance: data.new_balance }));
        if (data.win) {
          if (data.bet_type === "straight") { setMsg(`🎉 Straight hit ${data.result}! +${data.payout}`); SOUND.bigwin(); fireConfetti(); }
          else { setMsg(`${data.result} ${data.color} · +${data.payout}`); SOUND.win(); }
        } else { setMsg(`${data.result} ${data.color} · -${data.bet}`); SOUND.loss(); }
        setSpinning(false); onRefresh();
      }, 1500);
    } catch (e) { clearInterval(iv); setSpinning(false); setMsg(e?.response?.data?.detail || "Spin failed"); }
  };
  const RED_NUMS = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);
  const displayNum = spinning ? ballFrame : (result?.result ?? "?");
  const displayColor = spinning ? (RED_NUMS.has(ballFrame) ? "red" : (ballFrame === 0 ? "green" : "black")) : (result?.color || "gray");
  return (
    <div className="fl-card p-6 md:p-8 bg-black/70 border-yellow-500/30 text-center" data-testid="roulette-game">
      <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-yellow-500 mb-2">// Roulette · European (single 0)</div>
      <h2 className="fl-display text-3xl font-black">Pick a bet. Spin.</h2>
      <div className={`mt-6 w-40 h-40 mx-auto rounded-full flex items-center justify-center text-5xl font-black shadow-2xl transition ${displayColor === "red" ? "bg-red-600 text-white" : displayColor === "black" ? "bg-black text-white border-2 border-white/40" : displayColor === "green" ? "bg-emerald-600 text-white" : "bg-white/5 text-white/40"} ${spinning ? "animate-pulse" : ""}`} data-testid="roulette-wheel">
        {displayNum}
      </div>
      <div className="mt-6 flex justify-center gap-2 flex-wrap" data-testid="roulette-bet-types">
        {[
          { id: "red", label: "Red 2×" }, { id: "black", label: "Black 2×" },
          { id: "odd", label: "Odd 2×" }, { id: "even", label: "Even 2×" },
          { id: "low", label: "1–18 2×" }, { id: "high", label: "19–36 2×" },
          { id: "straight", label: "Straight 36×" },
        ].map(b => (
          <button key={b.id} onClick={() => { SOUND.click(); setBetType(b.id); }} disabled={spinning} className={`px-3 py-1.5 rounded-full text-xs font-bold ${betType === b.id ? "bg-yellow-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`roulette-bt-${b.id}`}>{b.label}</button>
        ))}
      </div>
      {betType === "straight" && (
        <div className="mt-3 inline-flex items-center gap-2">
          <span className="text-white/60 text-xs">Number:</span>
          <input type="number" min={0} max={36} value={number} onChange={e => setNumber(Math.max(0, Math.min(36, Number(e.target.value)||0)))} className="fl-input w-20 text-center" data-testid="roulette-straight-number" />
        </div>
      )}
      <BetPicker bet={bet} setBet={setBet} disabled={spinning} testidPrefix="roulette-bet" />
      <button onClick={spin} disabled={spinning || wallet.coin_balance < bet} className="fl-btn !bg-red-500 hover:!bg-red-400 !text-white mt-6 !py-3 !px-8 text-lg" data-testid="roulette-spin">
        {spinning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Dice5 className="w-5 h-5" />} {spinning ? "Spinning…" : `Spin for ${bet}`}
      </button>
      {msg && <div className="mt-4 text-yellow-300 text-sm" data-testid="roulette-msg">{msg}</div>}
    </div>
  );
}

function BetPicker({ bet, setBet, disabled, testidPrefix }) {
  return (
    <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
      <span className="text-white/60 text-sm">Bet:</span>
      {[10, 50, 100, 500, 1000].map(v => (
        <button key={v} onClick={() => { SOUND.click(); setBet(v); }} disabled={disabled}
          className={`px-3 py-1.5 rounded-full text-sm font-bold ${bet === v ? "bg-yellow-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
          data-testid={`${testidPrefix}-${v}`}>{v}</button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Coin shop
// ────────────────────────────────────────────────────────────
function CoinShop({ onClose, onPurchased }) {
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/casino/coin-packs"); setPacks(data || []); } catch (_) { /* silent */ } })();
  }, []);
  const buy = async (pkg) => {
    setBusy(pkg.package_id); SOUND.click();
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkg.package_id, origin_url: window.location.origin + "/casino/play" }, { headers: casinoAuthHeaders() });
      window.location.href = data.url;
    } catch (e) { alert(e?.response?.data?.detail || "Checkout failed"); setBusy(null); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-5" data-testid="coin-shop">
      <div className="fl-card max-w-xl w-full p-6 bg-[#12070a] border-yellow-500/40 relative">
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="coin-shop-close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <ShoppingBag className="w-5 h-5 text-yellow-500" />
          <div className="fl-mono text-[10px] uppercase tracking-widest text-yellow-500">// Coin shop</div>
        </div>
        <h2 className="fl-display text-2xl font-black">Top up virtual coins.</h2>
        <p className="text-white/60 text-xs mt-1">Consumable in-game currency. <strong className="text-yellow-300">No cash value.</strong> Cannot be redeemed for money or prizes.</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="coin-shop-packs">
          {packs.map(p => (
            <button key={p.package_id} onClick={() => buy(p)} disabled={busy === p.package_id}
              className="text-left rounded-2xl border border-yellow-500/30 bg-black/50 hover:bg-yellow-500/10 hover:border-yellow-400 p-4 transition"
              data-testid={`coin-shop-buy-${p.package_id}`}>
              <div className="text-3xl font-black text-yellow-400">{p.coins.toLocaleString()}</div>
              <div className="text-white/50 text-xs mt-0.5">coins</div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-white font-bold">${p.price_usd.toFixed(2)}</span>
                {busy === p.package_id ? <Loader2 className="w-4 h-4 animate-spin text-yellow-400" /> : <ChevronRight className="w-4 h-4 text-yellow-400" />}
              </div>
            </button>
          ))}
        </div>
        <p className="text-white/40 text-[10px] mt-4">Powered by Stripe. Fliboe Play is a social casino game for entertainment only.</p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Isolated header + legal footer — no FM / Flip nav
// ────────────────────────────────────────────────────────────
function CasinoHeader({ onLogout, balance }) {
  return (
    <header className="border-b border-yellow-500/20 backdrop-blur bg-black/80 sticky top-0 z-40">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-4">
        <Link to="/casino" className="flex items-center gap-2" data-testid="casino-brand">
          <div className="w-8 h-8 rounded-lg bg-yellow-500 text-black flex items-center justify-center font-black text-lg">P</div>
          <span className="fl-display text-xl font-extrabold tracking-tighter">FLIBOE <span className="text-yellow-500">PLAY</span></span>
        </Link>
        <nav className="flex items-center gap-3">
          {balance !== undefined ? (
            <span className="text-yellow-400 font-bold text-sm inline-flex items-center gap-1" data-testid="casino-header-balance">
              <Coins className="w-4 h-4" /> {balance.toLocaleString()}
            </span>
          ) : (
            <Link to="/casino/login" className="fl-btn ghost !py-2 !px-3 text-xs">Sign in</Link>
          )}
          {onLogout && (
            <button onClick={onLogout} className="fl-btn ghost !py-2 !px-3 text-xs" data-testid="casino-logout">
              <LogOut className="w-3.5 h-3.5" /> Exit
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

function CasinoLegalFooter() {
  return (
    <footer className="mt-16 border-t border-yellow-500/20 bg-black/80">
      <div className="max-w-4xl mx-auto px-5 py-8 text-center text-white/50 text-xs" data-testid="casino-legal-footer">
        <div className="flex items-center justify-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          <div className="font-mono uppercase tracking-widest text-yellow-500">18+ · Play-money game · Not real gambling</div>
        </div>
        <p className="max-w-2xl mx-auto leading-relaxed">
          Fliboe Play is a social casino game for entertainment only. Virtual coins have <strong className="text-white/70">no monetary value</strong>, cannot be exchanged for cash, prizes, or anything of value. Practice or success at social casino gaming does not imply future success at real-money gambling.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4 flex-wrap fl-mono uppercase tracking-widest text-white/40">
          <Link to="/terms" className="hover:text-yellow-400">Terms</Link>
          <Link to="/privacy" className="hover:text-yellow-400">Privacy</Link>
          <a href="https://www.ncpgambling.org" target="_blank" rel="noreferrer" className="hover:text-yellow-400">Responsible play</a>
        </div>
      </div>
    </footer>
  );
}

// ────────────────────────────────────────────────────────────
// Casino Google Auth callback — receives #session_id from Emergent,
// exchanges it for a Fliboe session_token, stores in the CASINO key,
// then jumps to /casino/play. Isolated from FM auth on purpose.
// ────────────────────────────────────────────────────────────
export function CasinoAuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) { navigate("/casino/login", { replace: true }); return; }
      try {
        const { data } = await api.post("/auth/session", { session_id: m[1] });
        if (!data?.session_token) throw new Error("No token");
        localStorage.setItem(CASINO_TOKEN_KEY, data.session_token);
        localStorage.setItem(CASINO_AGE_KEY, "1");
        window.history.replaceState({}, document.title, "/casino/play");
        navigate("/casino/play", { replace: true });
      } catch (_) {
        navigate("/casino/login", { replace: true });
      }
    })();
  }, [navigate]);
  return (
    <main className="min-h-screen bg-[#0a0505] text-white grid place-items-center" data-testid="casino-auth-callback">
      <div className="text-yellow-500 fl-mono text-xs uppercase tracking-widest">// Signing you in with Google…</div>
    </main>
  );
}

